import { apiClient } from '../lib/api';

export interface RateLimitConfig {
	windowMs: number;
	limit: number;
	lockoutMs: number;
	exemptPaths: string[];
}

export type HitReason = 'rate_exceeded' | 'lockout_active' | 'blacklisted';

export interface RateLimitHit {
	ts: number;
	ip: string;
	email?: string;
	path: string;
	method: string;
	userAgent?: string;
	reason: HitReason;
}

export interface BlacklistEntry {
	ip: string;
	reason: string;
	createdAt: number;
	expiresAt?: number | null;
	createdBy?: string | null;
}

export interface ActiveLockout {
	ip: string;
	expiresAt: number;
}

interface Envelope<T> { success: boolean; data: T; message?: string }

const BASE = '/admin/rate-limit';

export const rateLimitApi = {
	async getConfig(): Promise<RateLimitConfig> {
		const res = await apiClient.get<Envelope<RateLimitConfig>>(`${BASE}/config`);
		return res.data.data;
	},
	async patchConfig(body: Partial<RateLimitConfig>): Promise<RateLimitConfig> {
		const res = await apiClient.patch<Envelope<RateLimitConfig>>(`${BASE}/config`, body);
		return res.data.data;
	},
	async getRecentHits(limit = 50): Promise<RateLimitHit[]> {
		const res = await apiClient.get<Envelope<RateLimitHit[]>>(`${BASE}/hits/recent`, { limit });
		return res.data.data;
	},
	async downloadHits(from: string, to: string, format: 'jsonl' | 'csv'): Promise<void> {
		const filename = `rate-limit-hits-${from}-to-${to}.${format}`;
		await apiClient.downloadBlob(`${BASE}/hits/download`, { from, to, format }, filename);
	},
	async getBlacklist(): Promise<BlacklistEntry[]> {
		const res = await apiClient.get<Envelope<BlacklistEntry[]>>(`${BASE}/blacklist`);
		return res.data.data;
	},
	async addBlacklist(body: { ip: string; reason?: string; expiresAt?: number | null }): Promise<BlacklistEntry> {
		const res = await apiClient.post<Envelope<BlacklistEntry>>(`${BASE}/blacklist`, body);
		return res.data.data;
	},
	async removeBlacklist(ip: string): Promise<void> {
		await apiClient.delete<Envelope<unknown>>(`${BASE}/blacklist/${encodeURIComponent(ip)}`);
	},
	async getLockouts(): Promise<ActiveLockout[]> {
		const res = await apiClient.get<Envelope<ActiveLockout[]>>(`${BASE}/lockouts`);
		return res.data.data;
	},
	async unlockIp(ip: string): Promise<void> {
		await apiClient.delete<Envelope<unknown>>(`${BASE}/lockouts/${encodeURIComponent(ip)}`);
	},
};
